/**
 * Legal Center — content & metadata for all policy pages.
 *
 * Each section supports two shapes for `body`:
 *   1. A single string  — rendered as one paragraph (legacy).
 *   2. An array of blocks, each of shape:
 *      { type: 'paragraph', text: string }
 *      { type: 'bullets',   items: string[] }
 *      { type: 'numbered',  items: { title: string, text: string }[] }
 * The `LegalPage` renderer handles both transparently.
 */

export const LEGAL_PAGES = {
  'privacy-policy': {
    slug: 'privacy-policy',
    title: 'إشعار الخصوصية',
    titleEn: 'Privacy Notice',
    badge: 'حماية البيانات الشخصية',
    intro:
      'تحرص Childhood Horizons على حماية خصوصية البيانات الشخصية لجميع المستفيدين من خدماتها، وتلتزم بمعالجة البيانات الشخصية بمسؤولية وشفافية، وفقًا للأنظمة واللوائح المعمول بها في المملكة العربية السعودية، بما في ذلك نظام حماية البيانات الشخصية (PDPL) ولوائحه التنفيذية.',
    sections: [
      {
        heading: 'المقدمة',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تحرص Childhood Horizons على حماية خصوصية البيانات الشخصية لجميع المستفيدين من خدماتها، وتلتزم بمعالجة البيانات الشخصية بمسؤولية وشفافية، وفقًا للأنظمة واللوائح المعمول بها في المملكة العربية السعودية، بما في ذلك نظام حماية البيانات الشخصية (PDPL) ولوائحه التنفيذية. يهدف هذا الإشعار إلى توضيح كيفية جمع البيانات الشخصية واستخدامها والإفصاح عنها وحمايتها، وبيان حقوق أصحاب البيانات الشخصية وآلية ممارستها، بما يعزز الثقة والشفافية في جميع الخدمات التي يقدمها الموقع. ينطبق هذا الإشعار على جميع مستخدمي موقع Childhood Horizons وخدماته الإلكترونية، بما في ذلك العملاء، وزوار الموقع، والمتقدمون إلى بنك الكفاءات، وغيرهم من المستفيدين من الخدمات التي يقدمها الموقع.',
          },
        ],
      },
      {
        heading: 'البيانات الشخصية التي نجمعها',
        blocks: [
          {
            type: 'paragraph',
            text:
              'قد تجمع Childhood Horizons البيانات الشخصية اللازمة لتقديم خدماتها وتشغيل موقعها الإلكتروني، ويختلف نوع البيانات بحسب الخدمة التي يستخدمها المستفيد. وقد تشمل البيانات الشخصية — عند الاقتضاء — ما يلي:',
          },
          {
            type: 'bullets',
            items: [
              'بيانات الهوية: مثل الاسم الكامل.',
              'بيانات التواصل: مثل رقم الجوال والبريد الإلكتروني.',
              'البيانات الوظيفية والمهنية: مثل المسمى الوظيفي، وجهة العمل، والتخصص.',
              'بيانات الخدمات: مثل تفاصيل الطلبات، والاستفسارات، والعروض، والمراسلات المتعلقة بالخدمات.',
              'بيانات بنك الكفاءات: مثل السيرة الذاتية، والمؤهلات العلمية، والخبرات العملية، والشهادات المهنية، والرخص والمهارات، وأي مستندات يختار المتقدم رفعها.',
              'البيانات المالية: مثل بيانات الفواتير أو معلومات الدفع بالقدر اللازم لإتمام العمليات المالية، دون الاحتفاظ ببيانات وسائل الدفع الإلكترونية إذا كانت تتم معالجتها من خلال مزودي خدمات الدفع المعتمدين.',
              'البيانات التقنية: مثل عنوان بروتوكول الإنترنت (IP)، ونوع المتصفح، ونظام التشغيل، وبيانات الجهاز، وسجلات الاستخدام، وملفات تعريف الارتباط (Cookies) عند استخدام الموقع.',
              'أي بيانات أخرى يختار المستفيد تزويدنا بها عند التواصل معنا أو استخدام خدمات الموقع.',
            ],
          },
          {
            type: 'paragraph',
            text:
              'وتلتزم Childhood Horizons بجمع الحد الأدنى من البيانات الشخصية اللازمة لتحقيق الأغراض المحددة في هذا الإشعار، وعدم معالجة البيانات إلا في الحدود التي تقتضيها طبيعة الخدمات المقدمة والالتزامات النظامية.',
          },
        ],
      },
      {
        heading: 'كيفية جمع البيانات',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تقوم Childhood Horizons بجمع البيانات الشخصية بطرق نظامية ومشروعة، وبالقدر اللازم لتحقيق الأغراض الموضحة في هذه السياسة، وذلك من خلال الوسائل التالية:',
          },
          {
            type: 'bullets',
            items: [
              'البيانات التي يقدمها المستفيد مباشرة عند تعبئة النماذج الإلكترونية أو طلب الخدمات أو التسجيل في الموقع.',
              'البيانات المقدمة عند التواصل معنا عبر البريد الإلكتروني أو وسائل التواصل الرسمية أو النماذج المخصصة لذلك.',
              'البيانات المرفقة من قبل المستفيد، مثل السيرة الذاتية أو المستندات أو الملفات ذات الصلة بالخدمة المطلوبة.',
              'البيانات التي يتم جمعها تلقائيًا عند استخدام الموقع، مثل عنوان بروتوكول الإنترنت (IP)، ونوع المتصفح، ونظام التشغيل، وسجلات الاستخدام، وملفات تعريف الارتباط (Cookies)، وذلك لتحسين أداء الموقع وتعزيز تجربة المستخدم.',
              'البيانات التي يتم الحصول عليها من مصادر أخرى إذا كان ذلك مسموحًا به نظامًا، أو بناءً على موافقة صاحب البيانات عند الاقتضاء.',
            ],
          },
          {
            type: 'paragraph',
            text:
              'وتلتزم Childhood Horizons بجمع البيانات الشخصية بطريقة عادلة وشفافة، وعدم جمع أي بيانات إلا إذا كانت ضرورية لتحقيق غرض مشروع ومحدد.',
          },
        ],
      },
      {
        heading: 'أغراض معالجة البيانات',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تعالج Childhood Horizons البيانات الشخصية للأغراض المشروعة المرتبطة بطبيعة أعمالها وخدماتها، ويشمل ذلك ما يلي:',
          },
          {
            type: 'bullets',
            items: [
              'تقديم الخدمات الاستشارية والمهنية وتنفيذ الطلبات.',
              'التواصل مع العملاء والمستفيدين والرد على الاستفسارات والطلبات.',
              'إعداد العروض الفنية والمالية والعقود والفواتير، وإدارة العمليات المرتبطة بالخدمات.',
              'إدارة وتنظيم بنك الكفاءات، وتقييم الملفات المهنية، والتواصل مع المتقدمين بشأن الفرص أو المشاريع المناسبة عند توفرها.',
              'إدارة وتنفيذ البرامج التدريبية وورش العمل والفعاليات المهنية.',
              'تحسين جودة الخدمات، وتطوير الموقع، وتحسين تجربة المستخدم.',
              'حماية أمن الموقع وأنظمته، والكشف عن الاستخدام غير المشروع أو أي أنشطة قد تؤثر على سلامة الموقع أو مستخدميه.',
              'الامتثال للأنظمة واللوائح والتعليمات الصادرة عن الجهات المختصة في المملكة العربية السعودية، وتنفيذ الالتزامات النظامية أو التعاقدية.',
              'أي أغراض أخرى مرتبطة بالخدمات المقدمة، وبما يتوافق مع الأنظمة المعمول بها.',
            ],
          },
          {
            type: 'paragraph',
            text:
              'ولا تتم معالجة البيانات الشخصية لأغراض تتعارض مع الغرض الذي جُمعت من أجله، إلا إذا كان ذلك مسموحًا به نظامًا أو بعد الحصول على موافقة صاحب البيانات، متى كانت الموافقة مطلوبة نظامًا.',
          },
        ],
      },
      {
        heading: 'الأساس النظامي لمعالجة البيانات',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تعالج Childhood Horizons البيانات الشخصية استنادًا إلى واحد أو أكثر من الأسس النظامية المعتمدة، وذلك بحسب طبيعة الخدمة أو الغرض من المعالجة، ويشمل ذلك ما يلي:',
          },
          {
            type: 'bullets',
            items: [
              'موافقة صاحب البيانات الشخصية، في الحالات التي تتطلب فيها الأنظمة الحصول على الموافقة.',
              'تنفيذ اتفاق أو عقد يكون صاحب البيانات طرفًا فيه، أو اتخاذ الإجراءات اللازمة بناءً على طلبه قبل إبرام العقد.',
              'الالتزام بمتطلبات نظامية أو تنظيمية تفرضها الأنظمة أو الجهات المختصة في المملكة العربية السعودية.',
              'تحقيق مصلحة مشروعة لـ Childhood Horizons، بما لا يتعارض مع حقوق صاحب البيانات الشخصية أو مصالحه، ووفقًا لما تسمح به الأنظمة المعمول بها.',
            ],
          },
          {
            type: 'paragraph',
            text:
              'وتلتزم Childhood Horizons بعدم معالجة البيانات الشخصية إلا في حدود الغرض الذي جُمعت من أجله، أو لأي غرض آخر تسمح به الأنظمة ذات العلاقة.',
          },
        ],
      },
      {
        heading: 'مشاركة البيانات',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تحرص Childhood Horizons على المحافظة على سرية البيانات الشخصية، ولا يتم بيعها أو تأجيرها أو الإفصاح عنها للغير إلا في الحدود التي تقتضيها طبيعة الخدمات أو تسمح بها الأنظمة. وقد تتم مشاركة البيانات الشخصية في الحالات التالية:',
          },
          {
            type: 'bullets',
            items: [
              'مع المستشارين أو أعضاء فريق العمل المخولين، بالقدر اللازم لتقديم الخدمة أو تنفيذ المشروع.',
              'مع مزودي الخدمات التقنية أو التشغيلية الذين يدعمون تشغيل الموقع أو الخدمات الإلكترونية، مع التزامهم بحماية البيانات والمحافظة على سريتها.',
              'مع الجهات الحكومية أو القضائية أو الرقابية المختصة، متى كان ذلك مطلوبًا بموجب الأنظمة أو الأوامر النظامية.',
              'مع أطراف أخرى بناءً على موافقة صريحة من صاحب البيانات الشخصية، متى كانت الموافقة مطلوبة نظامًا.',
            ],
          },
          {
            type: 'paragraph',
            text:
              'وتلتزم Childhood Horizons بألا تتم مشاركة البيانات الشخصية إلا في الحدود اللازمة لتحقيق الغرض الذي جُمعت من أجله، ووفقًا للأنظمة والضوابط المعمول بها في المملكة العربية السعودية.',
          },
        ],
      },
      {
        heading: 'حماية البيانات الشخصية',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تلتزم Childhood Horizons بتطبيق التدابير التنظيمية والتقنية المناسبة لحماية البيانات الشخصية من الوصول غير المصرح به، أو الاستخدام، أو التعديل، أو الإفصاح، أو الإتلاف، أو الفقد، وذلك بما يتناسب مع طبيعة البيانات والمخاطر المحتملة. وتقتصر صلاحية الوصول إلى البيانات الشخصية على الأشخاص المخولين الذين تستلزم طبيعة أعمالهم الاطلاع عليها، ويلتزم جميع من يصرح لهم بالوصول إلى البيانات بالمحافظة على سريتها واستخدامها للأغراض المصرح بها فقط. كما تراجع Childhood Horizons إجراءات حماية البيانات بصورة دورية، وتعمل على تطويرها بما يتوافق مع المتطلبات النظامية وأفضل الممارسات المعمول بها.',
          },
          {
            type: 'paragraph',
            text:
              'ورغم حرص Childhood Horizons على اتخاذ التدابير المناسبة لحماية البيانات الشخصية، إلا أن نقل البيانات عبر شبكة الإنترنت لا يمكن ضمان أمنه بشكل كامل، وعليه لا يمكن تقديم ضمان مطلق بعدم حدوث أي مخاطر تقنية خارجة عن نطاق السيطرة. وفي حال وقوع أي حادث يؤثر على البيانات الشخصية، فسيتم التعامل معه واتخاذ الإجراءات اللازمة وفق الأنظمة واللوائح المعمول بها في المملكة العربية السعودية.',
          },
        ],
      },
      {
        heading: 'الاحتفاظ بالبيانات وإتلافها',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تحتفظ Childhood Horizons بالبيانات الشخصية طوال المدة اللازمة لتحقيق الأغراض التي جُمعت من أجلها، أو للمدة التي تقتضيها الأنظمة واللوائح والالتزامات النظامية أو التعاقدية، أيهما أطول. وعند انتهاء الغرض من معالجة البيانات الشخصية أو انقضاء مدة الاحتفاظ النظامية، يتم إتلاف البيانات أو حذفها أو إخفاء هويتها بطريقة آمنة، بما يضمن عدم إمكانية الوصول إليها أو استعادتها، وذلك ما لم يكن الاحتفاظ بها مطلوبًا بموجب نظام أو أمر قضائي أو التزام قانوني آخر. وتراجع Childhood Horizons مدد الاحتفاظ بالبيانات بصورة دورية للتأكد من توافقها مع الأغراض التي جُمعت من أجلها والمتطلبات النظامية ذات العلاقة.',
          },
        ],
      },
      {
        heading: 'ملفات تعريف الارتباط (Cookies)',
        blocks: [
          {
            type: 'paragraph',
            text:
              'يستخدم موقع Childhood Horizons ملفات تعريف الارتباط (Cookies) والتقنيات المشابهة لتحسين تجربة المستخدم، وتسهيل استخدام الموقع، وتحليل أداء الخدمات الإلكترونية. وقد تُستخدم ملفات تعريف الارتباط للأغراض التالية:',
          },
          {
            type: 'bullets',
            items: [
              'تمكين الوظائف الأساسية للموقع.',
              'تذكر تفضيلات المستخدم وإعداداته.',
              'تحسين أداء الموقع وتجربة الاستخدام.',
              'تحليل استخدام الموقع وتطوير الخدمات الإلكترونية.',
            ],
          },
          {
            type: 'paragraph',
            text:
              'يمكن للمستخدم التحكم في إعدادات ملفات تعريف الارتباط أو تعطيلها من خلال إعدادات متصفح الإنترنت، مع العلم أن تعطيل بعض الملفات قد يؤثر في أداء بعض وظائف الموقع أو الخدمات الإلكترونية. ولا تُستخدم ملفات تعريف الارتباط لجمع بيانات شخصية تتجاوز الأغراض الموضحة في هذا الإشعار، أو بما يخالف الأنظمة واللوائح المعمول بها.',
          },
        ],
      },
      {
        heading: 'حقوق أصحاب البيانات الشخصية',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تحرص Childhood Horizons على تمكين أصحاب البيانات الشخصية من ممارسة حقوقهم وفقًا لنظام حماية البيانات الشخصية والأنظمة ذات العلاقة في المملكة العربية السعودية، ويشمل ذلك — وفقًا للضوابط النظامية — ما يلي:',
          },
          {
            type: 'numbered',
            items: [
              {
                title: 'الحق في العلم',
                text:
                  'يحق لصاحب البيانات الشخصية معرفة كيفية جمع بياناته الشخصية، وأغراض معالجتها، والأساس النظامي لمعالجتها، والجهات التي قد تتم مشاركة البيانات معها، ومدة الاحتفاظ بها.',
              },
              {
                title: 'الحق في الوصول إلى البيانات الشخصية',
                text:
                  'يحق لصاحب البيانات الشخصية طلب الوصول إلى بياناته الشخصية والاطلاع عليها، والحصول على نسخة منها بالصيغة التي تسمح بها الأنظمة والضوابط المعمول بها.',
              },
              {
                title: 'الحق في تصحيح البيانات الشخصية',
                text:
                  'يحق لصاحب البيانات الشخصية طلب تصحيح بياناته الشخصية أو تحديثها أو استكمالها إذا كانت غير دقيقة أو غير مكتملة.',
              },
              {
                title: 'الحق في طلب إتلاف البيانات الشخصية',
                text:
                  'يحق لصاحب البيانات الشخصية طلب إتلاف بياناته الشخصية في الحالات التي يجيزها النظام، وذلك ما لم يكن الاحتفاظ بها مطلوبًا بموجب نظام أو أمر قضائي أو التزام تعاقدي أو قانوني.',
              },
              {
                title: 'الحق في سحب الموافقة',
                text:
                  'إذا كانت معالجة البيانات الشخصية تستند إلى موافقة صاحب البيانات، فيحق له سحب هذه الموافقة في أي وقت، ما لم يوجد أساس نظامي آخر يجيز استمرار المعالجة.',
              },
              {
                title: 'الحق في تقديم شكوى',
                text:
                  'يحق لصاحب البيانات الشخصية تقديم شكوى إلى الجهة المختصة إذا رأى أن معالجة بياناته الشخصية تمت بما يخالف الأنظمة المعمول بها.',
              },
            ],
          },
          {
            type: 'paragraph',
            text:
              'ولممارسة أي من هذه الحقوق، يمكن لصاحب البيانات الشخصية التواصل مع Childhood Horizons عبر وسائل التواصل الرسمية الموضحة في صفحة «اتصل بنا»، وسيتم التعامل مع الطلبات وفقًا للأنظمة والضوابط المعمول بها.',
          },
        ],
      },
      {
        heading: 'نقل البيانات',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تلتزم Childhood Horizons | آفاق الطفولة بعدم نقل البيانات الشخصية خارج المملكة العربية السعودية إلا في الحالات التي تسمح بها الأنظمة واللوائح المعمول بها، أو إذا اقتضت طبيعة الخدمة ذلك، مع اتخاذ التدابير اللازمة لضمان حماية البيانات الشخصية وفق المتطلبات النظامية.',
          },
        ],
      },
      {
        heading: 'تحديث إشعار الخصوصية',
        blocks: [
          {
            type: 'paragraph',
            text:
              'تحتفظ Childhood Horizons بحق تحديث أو تعديل هذا الإشعار عند الحاجة، بما يتوافق مع التغييرات النظامية أو التنظيمية أو التشغيلية، أو عند تطوير الخدمات التي يقدمها الموقع. ويُعمل بالإصدار المحدث من إشعار الخصوصية اعتبارًا من تاريخ نشره على الموقع، ويُنصح المستخدمون بمراجعة هذا الإشعار بشكل دوري للاطلاع على أي تحديثات. وفي حال ترتب على التحديث تغيير جوهري في كيفية معالجة البيانات الشخصية، فسيتم اتخاذ الإجراء المناسب لإشعار أصحاب البيانات، وذلك وفقًا لما تقتضيه الأنظمة واللوائح المعمول بها.',
          },
        ],
      },
      {
        heading: 'التواصل معنا',
        blocks: [
          {
            type: 'paragraph',
            text:
              'إذا كان لديك أي استفسار يتعلق بهذا الإشعار، أو رغبت في ممارسة أي من حقوقك المتعلقة بالبيانات الشخصية، أو تقديم طلب أو استفسار بشأن معالجة بياناتك الشخصية، فيمكنك التواصل مع Childhood Horizons من خلال وسائل التواصل الرسمية الموضحة في صفحة «اتصل بنا» بالموقع. وتلتزم Childhood Horizons بدراسة الطلبات والاستفسارات والتعامل معها وفقًا للأنظمة واللوائح المعمول بها في المملكة العربية السعودية.',
          },
        ],
      },
    ],
  },

  'terms-conditions': {
    slug: 'terms-conditions',
    title: 'الشروط والأحكام',
    titleEn: 'Terms & Conditions',
    badge: 'إطار التعامل الرسمي',
    intro:
      'تحكم هذه الشروط والأحكام استخدامك لموقع وخدمات Child Horizons — آفاق الطفولة. باستخدامك للموقع أو طلبك لأي خدمة، فإنك تقرّ بأنك قرأت هذه الشروط وفهمتها ووافقت عليها.',
    sections: [
      {
        heading: 'طبيعة الخدمات',
        body: 'تُقدِّم Child Horizons خدمات استشارية ومعرفية متخصصة في قطاع الطفولة المبكرة، تشمل: الاستشارات التربوية والإدارية، استشارات الجودة والاعتماد، تأسيس وتشغيل رياض الأطفال، تطوير السياسات، ورش العمل المهنية، استقطاب الكفاءات، الموارد المهنية، والبرامج المهنية المتكاملة.',
      },
      {
        heading: 'نطاق الخدمات والمسؤولية المهنية',
        body: 'تقدم Childhood Horizons خدمات استشارية ومهنية قائمة على الخبرة والإنتاج الفكري، لدعم المنشآت في فهم المتطلبات والأنظمة والأدلة المعلنة، وتقييم الجاهزية، وإعداد ومراجعة متطلبات التقديم، ودعم العميل في استكمال الإجراءات عبر القنوات الرسمية المعتمدة. ولا تمثل Childhood Horizons أي جهة حكومية أو تنظيمية، ولا تملك صلاحية إصدار التراخيص أو الاعتمادات أو ضمان الموافقة عليها، وتبقى القرارات النهائية من اختصاص الجهات المختصة.',
      },
      {
        heading: 'الأهلية',
        body: 'يُشترط أن يكون المستخدم بالغاً قانونياً أو ممثّلاً عن جهة اعتبارية، وأن يمتلك صلاحية الالتزام نيابة عن نفسه أو جهته. يحقّ لنا رفض تقديم الخدمة لأي مستخدم في حال عدم استيفاء شروط الأهلية.',
      },
      {
        heading: 'استخدام الموقع',
        body: 'يلتزم المستخدم باستخدام الموقع لأغراض مهنية مشروعة فقط، وعدم القيام بأي محاولة لاختراق أو تعطيل النظام، أو رفع محتوى مخالف، أو إساءة استخدام النماذج والموارد المهنية.',
      },
      {
        heading: 'الالتزامات المالية',
        body: 'تُحدَّد جميع الخدمات والمنتجات الرقمية بأسعارها المعلَنة. السداد يتم عبر بوابات الدفع المعتمدة. تخضع المنتجات الرقمية والاستشارات المخصصة لسياسة الاسترداد المنفصلة المنشورة على الموقع.',
      },
      {
        heading: 'الإنهاء والإيقاف',
        body: 'يحقّ لـ Child Horizons إيقاف أو إنهاء الوصول إلى الخدمات في حال مخالفة هذه الشروط، دون إخلال بأي حقوق أو تعويضات قد تترتب على الإخلال.',
      },
      {
        heading: 'القانون الواجب التطبيق',
        body: 'تخضع هذه الشروط لأنظمة المملكة العربية السعودية، وتختص الجهات القضائية المختصة بالمملكة بالنظر في أي نزاع قد ينشأ عن استخدام الموقع أو خدماته.',
      },
    ],
  },

  'refund-policy': {
    slug: 'refund-policy',
    title: 'سياسة الإلغاء والاسترداد',
    titleEn: 'Refund & Cancellation Policy',
    badge: 'الشفافية المالية',
    intro:
      'نلتزم بمعايير الشفافية والوضوح في كل تعاملاتنا المالية. هذه السياسة تنظّم حالات الإلغاء والاسترداد للخدمات والمنتجات الرقمية التي تقدّمها Child Horizons.',
    sections: [
      {
        heading: 'المنتجات الرقمية',
        body: 'بعد إتمام الشراء وإرسال رابط التحميل أو ملف بصيغة العميل (مثل الملفات الموقّعة بعلامة مائية باسمه)، يُعدّ المنتج مُسلَّماً ولا يقبل الاسترداد. في حال وجود خلل تقني يمنع التحميل، يُرجى التواصل خلال 7 أيام لاستلام بديل أو تصحيح المشكلة.',
      },
      {
        heading: 'الاستشارات المخصصة',
        body: 'في حال إلغاء جلسة استشارية قبل موعد التنفيذ بمدة لا تقل عن 48 ساعة، يحقّ للعميل استرداد كامل المبلغ. الإلغاء خلال أقل من 48 ساعة يُقتطع منه 50%. عدم الحضور يُعدّ تنفيذاً للجلسة ولا يستحق استرداداً.',
      },
      {
        heading: 'البرامج المهنية',
        body: 'الإلغاء قبل انطلاق البرنامج بأكثر من 14 يوماً: استرداد كامل. الإلغاء خلال 14 يوماً قبل الانطلاق: استرداد 50%. الإلغاء بعد بدء البرنامج: لا يستحق استرداداً، ويمكن نقل المقعد لمشاركة بديلة وفق سياسة التحويل.',
      },
      {
        heading: 'مشاريع تأسيس وتشغيل',
        body: 'تخضع المشاريع طويلة الأمد لاتفاقية ثنائية موقّعة تحدد شروط الإلغاء والاسترداد لكل مرحلة على حدة. يُرجى الرجوع إلى نص الاتفاقية الخاصة بالمشروع.',
      },
      {
        heading: 'إجراءات طلب الاسترداد',
        body: 'يُرسل طلب الاسترداد عبر البريد الرسمي مع ذكر رقم الطلب وأسباب الإلغاء. تتم دراسة الطلب خلال 7 أيام عمل، وتنفيذ الاسترداد المستحق خلال 14 يوم عمل وفق آلية الدفع الأصلية.',
      },
    ],
  },

  'intellectual-property': {
    slug: 'intellectual-property',
    title: 'سياسة الملكية الفكرية',
    titleEn: 'Intellectual Property Policy',
    badge: 'حماية المحتوى المهني',
    intro:
      'جميع المحتويات المنشورة على Child Horizons — آفاق الطفولة محمية بقوانين الملكية الفكرية، وتُمثل ملكية حصرية للمنصة أو من تتعاقد معهم. هذه السياسة توضح حقوق الاستخدام المسموحة والممنوعة.',
    sections: [
      {
        heading: 'حقوق ملكية المحتوى',
        body: 'تمتلك Child Horizons — آفاق الطفولة كامل حقوق التأليف والنشر للمواد التالية: المحتوى الاستشاري، الموارد المهنية، البرامج المهنية، المقالات، التصاميم، النماذج، الأدوات الرقمية، الهوية البصرية، والشعارات.',
      },
      {
        heading: 'الاستخدام المسموح',
        body: 'يُسمح للمستخدمين بتصفح المحتوى وقراءته، تحميل الموارد المجانية للاستخدام الداخلي غير التجاري، وطباعة المواد للاستخدام المهني الشخصي داخل المنشأة. أي استخدام يتجاوز هذا النطاق يتطلب إذناً خطياً مسبقاً.',
      },
      {
        heading: 'الاستخدام الممنوع',
        body: 'يُمنع منعاً باتاً: نسخ المحتوى أو إعادة نشره بأي وسيلة، بيع الموارد أو إعادة توزيعها، تعديل المحتوى وادّعاء ملكيته، استخدامه في إعداد منتجات منافسة، أو رفعه على منصات أخرى دون موافقة خطية موثّقة من Child Horizons.',
      },
      {
        heading: 'علامة مائية وتتبّع',
        body: 'تُدمَج علامات مائية رقمية في المنتجات المدفوعة لربط كل نسخة بالعميل المشتري. أي توزيع غير مصرح به سيُكشف ويُعرّض المسؤول لإجراءات قانونية.',
      },
      {
        heading: 'الإبلاغ عن الانتهاكات',
        body: 'في حال ملاحظة أي انتهاك لحقوق الملكية الفكرية لـ Child Horizons، يُرجى التواصل معنا عبر البريد الرسمي مع تقديم الأدلة. نتعهد بالتحقيق الجاد واتخاذ الإجراءات القانونية اللازمة.',
      },
    ],
  },

  'professional-disclaimer': {
    slug: 'professional-disclaimer',
    title: 'إخلاء المسؤولية',
    titleEn: 'Professional Disclaimer',
    badge: 'حدود الخدمة المهنية',
    intro:
      'تسعى Child Horizons — آفاق الطفولة لتقديم محتوى مهني عالي الجودة قائم على المعرفة المتخصصة والممارسة الميدانية. ومع ذلك، يجب فهم النطاق المهني لخدماتنا وحدود مسؤوليتها.',
    sections: [
      {
        heading: 'طبيعة الخدمات الاستشارية',
        body: 'تُقدَّم الاستشارات والتوصيات بناءً على المعلومات المتاحة وقت تقديم الخدمة، وهي تمثّل آراء مهنية اجتهادية. تتحمل الجهة المستفيدة مسؤولية اتخاذ قرارات التنفيذ النهائية وتقدير ملاءمتها لظروفها الخاصة.',
      },
      {
        heading: 'الموارد المهنية والقوالب',
        body: 'النماذج والقوالب المتاحة على المنصة هي أدوات إرشادية تساعد على تنظيم العمل، ولا تُغني عن المراجعة المهنية المتخصصة من قبل المنشأة أو مستشاريها القانونيين والتشغيليين.',
      },
      {
        heading: 'البرامج المهنية',
        body: 'تُمثّل البرامج المهنية مسارات تأهيل مهني وفق منهجية محددة. الشهادات الصادرة تعكس إتمام مسار التعلم، ولا تُمثّل اعتماداً حكومياً أو ترخيصاً مهنياً ما لم يُذكر صراحةً.',
      },
      {
        heading: 'بنك الكفاءات',
        body: 'لا تقدّم Child Horizons خدمات توظيف أو وساطة وظيفية أو ضمان للحصول على عمل. تقتصر خدمة بنك الكفاءات على بناء الملفات المهنية وتمكين التواصل المباشر بين الأطراف، ولا تُعدّ المنصة طرفاً في أي عقد توظيف.',
      },
      {
        heading: 'المعلومات المنشورة',
        body: 'المقالات والمعلومات المنشورة على الموقع لأغراض معرفية عامة، ولا يُغني الاطلاع عليها عن طلب استشارة مهنية متخصصة في كل حالة على حدة.',
      },
      {
        heading: 'حدود المسؤولية القانونية',
        body: 'Child Horizons لا تتحمل مسؤولية أي قرارات تنفيذية تتخذها الجهة المستفيدة بناءً على خدماتنا أو محتوياتنا، ولا أي خسائر مباشرة أو غير مباشرة تنشأ عن سوء التطبيق أو التطبيق دون استشارة كافية.',
      },
    ],
  },
};

export const LEGAL_LINKS = [
  { slug: 'privacy-policy', label: 'إشعار الخصوصية' },
  { slug: 'terms-conditions', label: 'شروط الاستخدام' },
  { slug: 'refund-policy', label: 'سياسة الإلغاء والاسترداد' },
  { slug: 'intellectual-property', label: 'سياسة الملكية الفكرية' },
  { slug: 'professional-disclaimer', label: 'إخلاء المسؤولية' },
];
